/**
 * 音视频通话状态管理
 */

const state = {
  // 当前通话状态
  callStatus: 'idle', // idle, outgoing, incoming, connecting, connected, ended
  
  // 通话模式
  callMode: 'audio', // audio, video
  
  // 当前通话ID
  currentCallId: null,
  
  // 频道名称
  channelName: null,
  
  // RTC Token
  rtcToken: null,
  
  // 远端用户ID
  remoteUserId: null,
  
  // 远端用户信息
  remoteUserInfo: {},
  
  // 是否是发起方
  isInitiator: false,
  
  // 呼入邀请列表（可能同时有多个呼入）
  incomingInvitations: []
}

const mutations = {
  // 设置通话状态
  SET_CALL_STATUS(state, status) {
    state.callStatus = status
  },
  
  // 设置通话模式
  SET_CALL_MODE(state, mode) {
    state.callMode = mode
  },
  
  // 设置当前通话ID
  SET_CURRENT_CALL_ID(state, callId) {
    state.currentCallId = callId
  },
  
  // 设置频道名称
  SET_CHANNEL_NAME(state, channelName) {
    state.channelName = channelName
  },
  
  // 设置RTC Token
  SET_RTC_TOKEN(state, token) {
    state.rtcToken = token
  },
  
  // 设置远端用户ID
  SET_REMOTE_USER_ID(state, userId) {
    state.remoteUserId = userId
  },
  
  // 设置远端用户信息
  SET_REMOTE_USER_INFO(state, userInfo) {
    state.remoteUserInfo = userInfo
  },
  
  // 设置是否是发起方
  SET_IS_INITIATOR(state, isInitiator) {
    state.isInitiator = isInitiator
  },
  
  // 添加呼入邀请
  ADD_INCOMING_INVITATION(state, invitation) {
    // 检查是否已存在
    const exists = state.incomingInvitations.some(
      inv => inv.callId === invitation.callId
    )
    if (!exists) {
      state.incomingInvitations.push(invitation)
    }
  },
  
  // 移除呼入邀请
  REMOVE_INCOMING_INVITATION(state, callId) {
    state.incomingInvitations = state.incomingInvitations.filter(
      inv => inv.callId !== callId
    )
  },
  
  // 清空所有呼入邀请
  CLEAR_INCOMING_INVITATIONS(state) {
    state.incomingInvitations = []
  },
  
  // 重置通话状态
  RESET_CALL_STATE(state) {
    state.callStatus = 'idle'
    state.currentCallId = null
    state.channelName = null
    state.rtcToken = null
    state.remoteUserId = null
    state.remoteUserInfo = {}
    state.isInitiator = false
  }
}

const actions = {
  // 发起通话
  initiateCall({ commit }, { callId, callMode, channelName, rtcToken, remoteUserId, remoteUserInfo }) {
    commit('SET_CALL_STATUS', 'outgoing')
    commit('SET_CALL_MODE', callMode)
    commit('SET_CURRENT_CALL_ID', callId)
    commit('SET_CHANNEL_NAME', channelName)
    commit('SET_RTC_TOKEN', rtcToken)
    commit('SET_REMOTE_USER_ID', remoteUserId)
    commit('SET_REMOTE_USER_INFO', remoteUserInfo)
    commit('SET_IS_INITIATOR', true)
  },
  
  // 接收通话邀请
  receiveCallInvitation({ commit }, invitation) {
    commit('ADD_INCOMING_INVITATION', invitation)
  },
  
  // 接受通话
  acceptCall({ commit, state }, { callId, channelName, rtcToken }) {
    const invitation = state.incomingInvitations.find(inv => inv.callId === callId)
    if (!invitation) {
      console.error('找不到通话邀请:', callId)
      return
    }
    
    commit('SET_CALL_STATUS', 'connecting')
    commit('SET_CALL_MODE', invitation.callMode)
    commit('SET_CURRENT_CALL_ID', callId)
    commit('SET_CHANNEL_NAME', channelName)
    commit('SET_RTC_TOKEN', rtcToken)
    commit('SET_REMOTE_USER_ID', invitation.caller)
    commit('SET_REMOTE_USER_INFO', invitation.callerInfo || {})
    commit('SET_IS_INITIATOR', false)
    commit('REMOVE_INCOMING_INVITATION', callId)
  },
  
  // 拒绝通话
  rejectCall({ commit }, callId) {
    commit('REMOVE_INCOMING_INVITATION', callId)
  },
  
  // 取消通话
  cancelCall({ commit }) {
    commit('RESET_CALL_STATE')
  },
  
  // 通话连接成功
  callConnected({ commit }) {
    commit('SET_CALL_STATUS', 'connected')
  },
  
  // 结束通话
  endCall({ commit }) {
    commit('SET_CALL_STATUS', 'ended')
    // 延迟重置状态，给UI一点时间显示结束状态
    setTimeout(() => {
      commit('RESET_CALL_STATE')
    }, 1000)
  }
}

const getters = {
  // 是否在通话中
  isInCall: state => {
    return ['outgoing', 'incoming', 'connecting', 'connected'].includes(state.callStatus)
  },
  
  // 是否有呼入邀请
  hasIncomingInvitation: state => {
    return state.incomingInvitations.length > 0
  },
  
  // 获取第一个呼入邀请
  firstIncomingInvitation: state => {
    return state.incomingInvitations[0] || null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

